import is from '@sindresorhus/is';
import { useCallback, useRef, useState } from 'react';

import { useCreateChatThread } from '../../api';
import { useChatMessagesCache } from '../api/use-chat-messages-cache';

export type UseChatThreadReturn = {
    readonly threadId: string | null;
    readonly isCreatingThread: boolean;
    readonly isFreshThread: boolean;
    readonly createFreshThread: () => Promise<string | null>;
    readonly ensureThreadId: () => Promise<string | null>;
    readonly resetThread: () => void;
    readonly selectThread: (id: string) => void;
};

export const useChatThread = (): UseChatThreadReturn => {
    const chatCache = useChatMessagesCache();
    const createThread = useCreateChatThread();

    const [threadId, setThreadId] = useState<string | null>(null);
    const freshThreadIdRef = useRef<string | null>(null);
    const createSeqRef = useRef(0);

    // Returns null when a newer creation started while this one was in flight,
    // so overlapping creations resolve to the latest thread instead of racing.
    const createFreshThread = useCallback(async (): Promise<string | null> => {
        const seq = ++createSeqRef.current;
        const thread = await createThread.mutateAsync();
        if (createSeqRef.current !== seq) return null;
        freshThreadIdRef.current = thread.id;
        chatCache.seedEmptyMessages(thread.id);
        setThreadId(thread.id);
        return thread.id;
    }, [createThread, chatCache]);

    const ensureThreadId = useCallback(async (): Promise<string | null> => {
        if (!is.null(threadId)) return threadId;
        return createFreshThread();
    }, [threadId, createFreshThread]);

    const selectThread = useCallback((id: string): void => {
        freshThreadIdRef.current = null;
        setThreadId(id);
    }, []);

    const resetThread = useCallback((): void => {
        freshThreadIdRef.current = null;
        setThreadId(null);
    }, []);

    const isFreshThread = !is.null(threadId) && freshThreadIdRef.current === threadId;

    return {
        createFreshThread,
        ensureThreadId,
        isCreatingThread: createThread.isPending,
        isFreshThread,
        resetThread,
        selectThread,
        threadId,
    };
};
